import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinDialog } from "@/components/PinDialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { formatMoney, parseAmountToCents } from "@/lib/money";
import { Bluetooth, Loader2, RefreshCw, ShieldAlert, WifiOff } from "lucide-react";
import { toast } from "sonner";
import {
  getOrCreateKeypair,
  registerDeviceKey,
  signVoucher,
  getPublicKeyB64,
} from "@/lib/ble/keys";
import { buildVoucher } from "@/lib/ble/voucher";
import { scanNearby, sendVoucherToPeer, type NearbyPeer } from "@/lib/ble/transport";
import { enqueueVoucher } from "@/lib/ble/queue";

type Step = "amount" | "scan" | "confirm";

const SendNearby = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>("amount");
  const [amount, setAmount] = useState("10.00");
  const [note, setNote] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [pinPurpose, setPinPurpose] = useState<"register" | "send">("send");
  const [pending, setPending] = useState(false);
  const [peers, setPeers] = useState<NearbyPeer[]>([]);
  const [scanning, setScanning] = useState(false);
  const [peer, setPeer] = useState<NearbyPeer | null>(null);
  const [hasKey, setHasKey] = useState<boolean>(false);

  useEffect(() => {
    void (async () => {
      const existing = await getPublicKeyB64();
      if (existing) setHasKey(true);
      await getOrCreateKeypair();
      setHasKey(true);
    })();
  }, []);

  const cents = parseAmountToCents(amount) ?? 0;
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;

  const handleAmountNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (cents <= 0) return toast.error("Enter an amount");
    setStep("scan");
    void doScan();
  };

  const doScan = async () => {
    setScanning(true);
    try {
      const found = await scanNearby(5000);
      setPeers(found);
      if (!found.length) toast.message("No nearby Cash Isle users found");
    } finally {
      setScanning(false);
    }
  };

  const selectPeer = (p: NearbyPeer) => {
    setPeer(p);
    setStep("confirm");
  };

  const startConfirm = () => {
    if (!peer || !user) return;
    setPinPurpose("send");
    setPinOpen(true);
  };

  const handlePin = async (pin: string) => {
    if (!user || !peer) return;
    setPending(true);
    try {
      // Make sure key is registered server-side
      if (pinPurpose === "register") {
        await registerDeviceKey(pin, navigator.userAgent.slice(0, 80));
        toast.success("Device registered");
        setPinOpen(false);
        return;
      }

      // Ensure device key registered (idempotent)
      try {
        await registerDeviceKey(pin, navigator.userAgent.slice(0, 80));
      } catch (e) {
        toast.error((e as Error).message);
        setPending(false);
        return;
      }

      const { sk } = await getOrCreateKeypair();
      const base = buildVoucher({
        senderId: user.id,
        receiverId: peer.user_id,
        amountCents: cents,
        note: note || null,
      });
      const sig = await signVoucher(sk, base);
      const voucher = { ...base, sender_signature: sig, receiver_device_id: peer.device_id };

      // If online, ask server to place hold + record voucher first
      if (online) {
        const { data, error } = await supabase.functions.invoke("ble-voucher-reserve", {
          body: {
            voucher_id: voucher.id,
            receiver_id: voucher.receiver_id,
            amount_cents: voucher.amount_cents,
            nonce: voucher.nonce,
            sender_device_id: voucher.sender_device_id,
            receiver_device_id: voucher.receiver_device_id,
            sender_signature: voucher.sender_signature,
            currency: voucher.currency,
            note: voucher.note,
          },
        });
        const err = (error as any)?.message || (data as any)?.error;
        if (err) {
          toast.error(err);
          setPending(false);
          return;
        }
      }

      // Hand off over BLE (or sim)
      try {
        await sendVoucherToPeer(peer, JSON.stringify(voucher));
      } catch (e) {
        toast.error((e as Error).message);
      }

      // Locally queue so sender can sync if receiver disappears
      await enqueueVoucher({
        id: voucher.id,
        role: "sender",
        payload: voucher,
        created_at: Date.now(),
        status: "queued",
      });

      setPinOpen(false);
      toast.success(`Sent ${formatMoney(cents)} via Bluetooth — settling…`);
      navigate("/history", { replace: true });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold flex items-center justify-center gap-2">
          <Bluetooth className="h-6 w-6" /> Nearby Transfer
        </h1>
      </BrandHeader>

      <div className="px-5 py-6 flex-1 space-y-5 animate-fade-in-up">
        {!online && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
            <WifiOff className="h-5 w-5 text-amber-600 shrink-0" />
            <span>Offline mode — voucher will sync when you reconnect.</span>
          </div>
        )}

        {step === "amount" && (
          <form onSubmit={handleAmountNext} className="space-y-5">
            <div>
              <Label className="text-sm font-medium">Amount</Label>
              <div className="mt-2 flex items-center bg-secondary/60 rounded-xl px-5 h-16">
                <span className="font-display text-3xl font-bold text-muted-foreground mr-1">$</span>
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-transparent flex-1 font-display text-3xl font-bold outline-none w-full"
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Daily nearby limit: $200 / 5 transfers
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={140}
                placeholder="What's it for?"
                className="mt-2 h-12 rounded-xl bg-secondary/60 border-transparent"
              />
            </div>
            <Button type="submit" variant="hero" size="xl" className="w-full" disabled={cents <= 0}>
              Continue
            </Button>
          </form>
        )}

        {step === "scan" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-semibold">Nearby users</h2>
                <p className="text-xs text-muted-foreground">
                  Sending {formatMoney(cents)}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={doScan} disabled={scanning}>
                {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Rescan
              </Button>
            </div>
            {scanning && (
              <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                <Bluetooth className="h-8 w-8 mx-auto mb-2 animate-pulse text-primary" />
                Scanning for nearby devices…
              </div>
            )}
            {!scanning && peers.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                No nearby Cash Isle users yet. Make sure the other person has Cash Isle open on the
                <strong> Receive Nearby</strong> screen.
              </div>
            )}
            <div className="space-y-2">
              {peers.map((p) => (
                <button
                  key={p.device_id}
                  onClick={() => selectPeer(p)}
                  className="w-full flex items-center justify-between rounded-xl bg-secondary/60 px-4 py-3 hover:bg-secondary transition"
                >
                  <div className="text-left">
                    <div className="font-medium">{p.display_name}</div>
                    {p.cashtag && <div className="text-xs text-muted-foreground">${p.cashtag}</div>}
                  </div>
                  {typeof p.rssi === "number" && (
                    <div className="text-xs text-muted-foreground">{p.rssi} dBm</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "confirm" && peer && (
          <div className="space-y-5">
            <div className="rounded-2xl bg-gradient-hero text-primary-foreground p-6 text-center">
              <div className="text-xs uppercase tracking-wider opacity-80">Sending</div>
              <div className="font-display text-4xl font-bold mt-1">{formatMoney(cents)}</div>
              <div className="mt-3 text-sm opacity-90">to {peer.display_name}</div>
            </div>
            <div className="rounded-xl border bg-card p-4 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span>Bluetooth (signed voucher)</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Settlement</span><span>{online ? "Pre-authorized" : "On reconnect"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Expires</span><span>72h</span></div>
            </div>
            {!hasKey && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldAlert className="h-4 w-4" /> First-time device registration required (PIN)
              </div>
            )}
            <Button variant="hero" size="xl" className="w-full" onClick={startConfirm} disabled={pending}>
              {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : `Confirm & send ${formatMoney(cents)}`}
            </Button>
          </div>
        )}
      </div>

      <PinDialog
        open={pinOpen}
        title="Confirm with PIN"
        description="Your PIN signs this transfer locally."
        onCancel={() => setPinOpen(false)}
        onSubmit={handlePin}
        pending={pending}
      />
      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default SendNearby;
